import { Badge, Button, Card, CardContent, Input, Label, Spinner } from '@fc/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, CheckCircle2, History, Play, Plus, Save, Trash2, XCircle } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { PageHeader } from '../../components/page-header'
import { listLists } from '../../lib/lists-api'
import {
  deleteWorkflow,
  disableWorkflow,
  enableWorkflow,
  getWorkflow,
  testWorkflow,
  updateWorkflow,
  type WorkflowActionInput,
  type WorkflowTestResult,
  type WorkflowTrigger,
  type WorkflowTriggerType,
} from '../../lib/workflows-api'
import { ActionEditor } from './action-editor'
import { ACTION_OPTIONS, defaultActionConfig, TRIGGER_OPTIONS } from './shared'

const selectClass =
  'flex h-10 w-full rounded-md border border-border bg-bg-alt px-3 py-2 text-sm text-fg ' +
  'focus-visible:outline-none focus-visible:border-secondary focus-visible:ring-[3px] focus-visible:ring-ring'

export function WorkflowDetail() {
  const { id = '' } = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['workflows', id],
    queryFn: () => getWorkflow(id),
    enabled: Boolean(id),
  })
  const { data: lists } = useQuery({ queryKey: ['lists'], queryFn: listLists })

  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [trigger, setTrigger] = useState<WorkflowTrigger>({ type: 'signup.created', config: {} })
  const [actions, setActions] = useState<WorkflowActionInput[]>([])

  useEffect(() => {
    if (data) {
      setName(data.name)
      setDescription(data.description ?? '')
      setTrigger(data.trigger)
      setActions(data.actions.map((a) => ({ type: a.type, config: a.config })))
    }
  }, [data])

  const dirty = useMemo(() => {
    if (!data) return false
    return (
      name !== data.name ||
      (description ?? '') !== (data.description ?? '') ||
      JSON.stringify(trigger) !== JSON.stringify(data.trigger) ||
      JSON.stringify(actions) !==
        JSON.stringify(data.actions.map((a) => ({ type: a.type, config: a.config })))
    )
  }, [data, name, description, trigger, actions])

  const save = useMutation({
    mutationFn: () =>
      updateWorkflow(id, { name, description: description || null, trigger, actions }),
    onSuccess: (updated) => {
      queryClient.setQueryData(['workflows', id], updated)
      queryClient.invalidateQueries({ queryKey: ['workflows'] })
    },
  })

  const toggle = useMutation({
    mutationFn: () => (data?.enabled ? disableWorkflow(id) : enableWorkflow(id)),
    onSuccess: (updated) => {
      queryClient.setQueryData(['workflows', id], updated)
      queryClient.invalidateQueries({ queryKey: ['workflows'] })
    },
  })

  const test = useMutation({ mutationFn: () => testWorkflow(id) })

  const remove = useMutation({
    mutationFn: () => deleteWorkflow(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workflows'] })
      navigate('/app/workflows')
    },
  })

  if (isLoading) {
    return (
      <div className="flex items-center justify-center gap-2 py-24 text-muted">
        <Spinner /> Loading workflow…
      </div>
    )
  }

  if (isError || !data) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/app/workflows')}>
          <ArrowLeft />
          Back to workflows
        </Button>
        <Card>
          <CardContent className="py-16 text-center">
            <p className="text-sm text-danger">Couldn’t load this workflow.</p>
            <p className="mt-1 text-xs text-dim">
              {error instanceof Error ? error.message : 'It may have been deleted.'}
            </p>
          </CardContent>
        </Card>
      </div>
    )
  }

  const addAction = () => {
    setActions((prev) => [
      ...prev,
      { type: 'add_to_list', config: defaultActionConfig('add_to_list') },
    ])
  }

  return (
    <div className="space-y-6">
      <div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/app/workflows')}
          className="mb-3"
        >
          <ArrowLeft />
          Back to workflows
        </Button>
        <PageHeader
          title={data.name}
          description="When the trigger fires, the engine runs the actions in order, asynchronously."
          actions={
            <>
              <Button
                variant="ghost"
                onClick={() => remove.mutate()}
                disabled={remove.isPending}
                aria-label="Delete workflow"
              >
                <Trash2 />
                Delete
              </Button>
              <Button
                variant="secondary"
                onClick={() => toggle.mutate()}
                disabled={toggle.isPending}
              >
                {data.enabled ? 'Disable' : 'Enable'}
              </Button>
              <Button onClick={() => save.mutate()} disabled={!dirty || save.isPending}>
                {save.isPending ? <Spinner /> : <Save />}
                Save
              </Button>
            </>
          }
        />
      </div>

      <div className="grid gap-6 lg:grid-cols-[1fr_22rem]">
        {/* Editor column */}
        <div className="space-y-6">
          <Card>
            <CardContent className="space-y-4 pt-6">
              <div className="space-y-1.5">
                <Label htmlFor="wf-name">Name</Label>
                <Input
                  id="wf-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  maxLength={160}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="wf-desc">Description</Label>
                <Input
                  id="wf-desc"
                  value={description}
                  placeholder="What does this automation do?"
                  onChange={(e) => setDescription(e.target.value)}
                  maxLength={2000}
                />
              </div>
            </CardContent>
          </Card>

          {/* Trigger */}
          <div className="space-y-2">
            <h2 className="font-display text-sm font-semibold uppercase tracking-wider text-dim">
              When this happens
            </h2>
            <Card>
              <CardContent className="space-y-3 pt-6">
                <div className="space-y-1.5">
                  <Label htmlFor="wf-trigger">Trigger</Label>
                  <select
                    id="wf-trigger"
                    className={selectClass}
                    value={trigger.type}
                    onChange={(e) =>
                      setTrigger({ type: e.target.value as WorkflowTriggerType, config: {} })
                    }
                  >
                    {TRIGGER_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </div>

                {(trigger.type === 'list.member_added' ||
                  trigger.type === 'signup.created' ||
                  trigger.type === 'form.submitted') && (
                  <TriggerFilter trigger={trigger} lists={lists ?? []} onChange={setTrigger} />
                )}

                {trigger.type === 'schedule' && (
                  <p className="text-xs text-dim">
                    Scheduled triggers are enqueued by the background worker (a follow-up). For now,
                    use Test to dry-run the actions.
                  </p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Actions */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-sm font-semibold uppercase tracking-wider text-dim">
                Do this
              </h2>
              <Button variant="secondary" size="sm" onClick={addAction}>
                <Plus />
                Add step
              </Button>
            </div>
            {actions.length === 0 ? (
              <Card>
                <CardContent className="py-10 text-center text-sm text-muted">
                  No steps yet. Add a step to define what should happen.
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {actions.map((action, i) => (
                  <ActionEditor
                    // index identity is stable enough for this small editable list
                    key={i}
                    index={i}
                    action={action}
                    lists={lists ?? []}
                    onChange={(next) =>
                      setActions((prev) => prev.map((a, j) => (j === i ? next : a)))
                    }
                    onRemove={() => setActions((prev) => prev.filter((_, j) => j !== i))}
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Sidebar: status, test, runs */}
        <div className="space-y-4">
          <Card>
            <CardContent className="space-y-3 pt-6">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Status</span>
                <Badge variant={data.enabled ? 'success' : 'neutral'}>
                  {data.enabled ? 'Enabled' : 'Disabled'}
                </Badge>
              </div>
              <p className="text-sm text-muted">
                {data.enabled
                  ? 'This workflow runs automatically when its trigger fires.'
                  : 'Enable this workflow to run it automatically.'}
              </p>
              {save.isError && (
                <p className="text-xs text-danger">
                  {save.error instanceof Error ? save.error.message : 'Save failed.'}
                </p>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardContent className="space-y-3 pt-6">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Test run</span>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => test.mutate()}
                  disabled={test.isPending || dirty}
                >
                  {test.isPending ? <Spinner /> : <Play />}
                  Dry-run
                </Button>
              </div>
              {dirty ? (
                <p className="text-xs text-dim">Save your changes before testing.</p>
              ) : (
                <p className="text-xs text-dim">
                  A dry-run validates every step and reports what would happen — without changing
                  any data or calling webhooks.
                </p>
              )}
              {test.isError && (
                <p className="text-xs text-danger">
                  {test.error instanceof Error ? test.error.message : 'Test failed.'}
                </p>
              )}
              {test.data && <TestResult result={test.data} />}
            </CardContent>
          </Card>

          <Card>
            <CardContent className="space-y-3 pt-6">
              <div className="flex items-center gap-2 text-sm font-medium">
                <History className="size-4 text-secondary" />
                Run history
              </div>
              <p className="text-sm text-muted">
                Every run (including dry-runs) is recorded immutably.
              </p>
              <Button asChild variant="outline" className="w-full">
                <Link to={`/app/workflows/${id}/runs`}>View run history</Link>
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}

function TriggerFilter({
  trigger,
  lists,
  onChange,
}: {
  trigger: WorkflowTrigger
  lists: { id: string; name: string }[]
  onChange: (t: WorkflowTrigger) => void
}) {
  const config = trigger.config ?? {}
  const set = (patch: Record<string, unknown>) =>
    onChange({ type: trigger.type, config: { ...config, ...patch } })

  if (trigger.type === 'list.member_added') {
    return (
      <div className="space-y-1.5">
        <Label htmlFor="trigger-list">Only for list (optional)</Label>
        <select
          id="trigger-list"
          className={selectClass}
          value={(config.listId as string) ?? ''}
          onChange={(e) => set({ listId: e.target.value || undefined })}
        >
          <option value="">Any list</option>
          {lists.map((l) => (
            <option key={l.id} value={l.id}>
              {l.name}
            </option>
          ))}
        </select>
      </div>
    )
  }

  const key = trigger.type === 'signup.created' ? 'eventId' : 'formId'
  const label =
    trigger.type === 'signup.created'
      ? 'Only for event id (optional)'
      : 'Only for form id (optional)'
  return (
    <div className="space-y-1.5">
      <Label htmlFor="trigger-filter">{label}</Label>
      <Input
        id="trigger-filter"
        value={(config[key] as string) ?? ''}
        placeholder="Leave blank to match all"
        onChange={(e) => set({ [key]: e.target.value || undefined })}
      />
    </div>
  )
}

function TestResult({ result }: { result: WorkflowTestResult }) {
  return (
    <div className="space-y-2 rounded-md border border-border bg-bg-alt p-3">
      <div className="flex items-center gap-2 text-sm">
        {result.status === 'failed' ? (
          <XCircle className="size-4 text-danger" />
        ) : (
          <CheckCircle2 className="size-4 text-success" />
        )}
        <span className="font-medium capitalize">{result.status}</span>
      </div>
      {result.steps.length === 0 ? (
        <p className="text-xs text-dim">No steps to run.</p>
      ) : (
        <ul className="space-y-1.5">
          {result.steps.map((s, i) => (
            <li key={i} className="flex items-start gap-2 text-xs">
              <span
                className={
                  s.status === 'error'
                    ? 'text-danger'
                    : s.status === 'skipped'
                      ? 'text-dim'
                      : 'text-success'
                }
              >
                {s.status === 'error' ? '✕' : s.status === 'skipped' ? '–' : '✓'}
              </span>
              <span className="text-muted">{s.detail}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
